import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { LoaderCircle } from "lucide-react";
import clsx from "clsx";

/**
 * Custom Button component with various styles and configurations
 * @param {Object} props - The props object
 * @param {React.ReactNode} [props.children] - The content to display inside the button
 * @param {Function} [props.onClick] - Click event handler
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.loading=false] - Whether to show loading spinner
 * @param {'primary'|'secondary'|'destructive'|'outlined'|'light'} [props.variant='primary'] - The button style variant
 * @param {boolean} [props.fullWidth=false] - Whether the button should take full width
 * @param {'extraSmall'|'small'|'medium'|'large'} [props.size='medium'] - The button size
 * @param {React.ReactNode} [props.startIcon] - Icon to display at the start of the button
 * @param {React.ReactNode} [props.endIcon] - Icon to display at the end of the button
 * @param {boolean} [props.iconButton=false] - Whether this is an icon-only button
 * @param {Object} [props...rest] - Any additional HTML button attributes
 */
const Button = ({ children, onClick, disabled = false, loading = false, variant = "primary", fullWidth = false, size = "medium", startIcon, endIcon, iconButton = false, ...props }) => {
  const classes = useStyles();

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const hasStartIcon = !!startIcon;
  const hasEndIcon = !!endIcon && !loading; // don't apply endIcon style if loading

  const sizeClass = classes[size];
  const iconPadClass = clsx({
    [classes[`${size}WithStartIcon`]]: hasStartIcon && !hasEndIcon,
    [classes[`${size}WithEndIcon`]]: hasEndIcon && !hasStartIcon,
    [classes[`${size}WithBothIcons`]]: hasStartIcon && hasEndIcon,
  });

  const iconButtonClass = clsx({
    [classes[`${size}IconButton`]]: iconButton,
  });

  const spinnerSize = size === "small" ? 14 : size === "large" ? 20 : 18;

  return (
    <button className={clsx(classes.root, classes[variant], sizeClass, iconPadClass, fullWidth && classes.fullWidth, iconButtonClass, loading && classes.loading)} onClick={onClick} disabled={disabled || loading} {...props}>
      {iconButton && startIcon && <span className={clsx(classes.icon, classes[`icon${capitalize(size)}`])}>{startIcon}</span>}
      {!iconButton && startIcon && <span className={clsx(classes.icon, classes.startIcon, classes[`icon${capitalize(size)}`])}>{startIcon}</span>}
      {!iconButton && !loading && children}
      {loading && <LoaderCircle size={spinnerSize} className={classes.spinner} />}
      {!iconButton && !loading && endIcon && <span className={clsx(classes.icon, classes.endIcon, classes[`icon${capitalize(size)}`])}>{endIcon}</span>}
    </button>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    fontWeight: "normal",
    fontFamily: "Lato, sans-serif",
    whiteSpace: "nowrap",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.3s, color 0.3s, opacity 0.3s",
    "&:disabled": {
      backgroundColor: "#DCDCDC",
      color: "#989898",
      cursor: "not-allowed",
    },
  },
  loading: {
    opacity: 0.7,
  },
  // Variant styles
  primary: {
    backgroundColor: "#C3996B",
    color: "#fff",
    "&:hover:not(:disabled)": {
      backgroundColor: "#B18B61",
    },
  },
  secondary: {
    backgroundColor: "#F8F8F8",
    color: "rgba(12, 14, 41, 0.8)",
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
      color: "rgba(12, 14, 41, 0.8)",
    },
  },
  destructive: {
    backgroundColor: "#D84949",
    color: "#F8F8F8",
    "&:hover:not(:disabled)": {
      backgroundColor: "#C54242",
    },
  },
  outlined: {
    backgroundColor: "transparent",
    border: "1px solid #C3996B",
    color: "#C3996B",
    "&:hover:not(:disabled)": {
      backgroundColor: "#B18B61",
      border: "1px solid #B18B61",
      color: "#fff",
    },
  },
  ghost: {
    backgroundColor: "transparent",
    color: "#0C0E29",
    opacity: 0.8,
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
      opacity: 1,
    },
  },
  light: {
    backgroundColor: "#F8F8F8",
    color: "#0C0E29",
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
      color: "#0C0E29",
    },
  },
  fullWidth: {
    width: "100%",
  },
  // Size variants
  extraSmall: {
    height: "28px",
    padding: "0 8px",
    fontSize: 16,
  },
  small: {
    height: "32px",
    padding: "0 8px",
    fontSize: 16,
  },
  medium: {
    height: "40px",
    padding: "0 16px",
    fontSize: 16,
  },
  large: {
    padding: "14px 26px",
    fontSize: 18,
  },
  // Icon styles
  icon: {
    display: "inline-flex",
    alignItems: "center",
  },
  // Icon sizes
  iconExtraSmall: {
    "& svg": {
      width: 16,
      height: 16,
    },
  },
  iconSmall: {
    "& svg": {
      width: 16,
      height: 16,
    },
  },
  iconMedium: {
    "& svg": {
      width: 20,
      height: 20,
      strokeWidth: 2,
    },
  },
  iconLarge: {
    "& svg": {
      width: 24,
      height: 24,
    },
  },
  // Padding for icon combinations
  extraSmallWithStartIcon: {
    paddingLeft: 6,
  },
  extraSmallWithEndIcon: {
    paddingRight: 6,
  },
  extraSmallWithBothIcons: {
    paddingLeft: 6,
    paddingRight: 6,
  },

  smallWithStartIcon: {
    paddingLeft: 6,
  },
  smallWithEndIcon: {
    paddingRight: 6,
  },
  smallWithBothIcons: {
    paddingLeft: 6,
    paddingRight: 6,
  },

  mediumWithStartIcon: {
    paddingLeft: 10,
  },
  mediumWithEndIcon: {
    paddingRight: 10,
  },
  mediumWithBothIcons: {
    paddingLeft: 10,
    paddingRight: 10,
  },

  largeWithStartIcon: {
    paddingLeft: 18,
  },
  largeWithEndIcon: {
    paddingRight: 18,
  },
  largeWithBothIcons: {
    paddingLeft: 18,
    paddingRight: 18,
  },
  // default icon margins
  startIcon: {
    marginRight: 8,
  },
  endIcon: {
    marginLeft: 8,
  },
  // Loading spinner
  spinner: {
    color: "#FFFFFF",
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  // IconButton styles
  extraSmallIconButton: {
    padding: "0 .25rem",
    width: "28px",
    height: "28px",
    justifyContent: "center",
    "& $icon": {
      margin: 0,
    },
  },

  smallIconButton: {
    padding: "0 .25rem",
    width: "32px",
    height: "32px",
    justifyContent: "center",
    "& $icon": {
      margin: 0,
    },
  },

  mediumIconButton: {
    padding: "0 .625rem",
    width: "40px",
    height: "40px",
    justifyContent: "center",
    "& $icon": {
      margin: 0,
    },
  },

  largeIconButton: {
    padding: "0 1.25rem",
    width: "48px",
    height: "48px",
    justifyContent: "center",
    "& $icon": {
      margin: 0,
    },
  },
}));

export default Button;
